import shaka from 'shaka-player';
import localConfig from 'config/localConfig';

import { PlayerAsset } from 'types/Player';
import { isSafari } from 'utils/fnDevices';
import { generateUUID } from 'utils/fnData';
import { guardedLocalStorage } from 'utils/fnStorage';
import { IDRM } from './abstract/IDRM';

class DRM extends IDRM {
    private drmType: string;

    private deviceId: string;

    private mediaKeysPreloaded = false;

    private currentAsset: PlayerAsset = null;

    private filtersRegisteredOn: any = null;

    private getOrCreateCxDeviceId = (drmClientType) => {
        const { DEVICE_ID_STORAGE } = localConfig.drm ?? {};
        const storageKey = DEVICE_ID_STORAGE + drmClientType;

        let cxDeviceId = guardedLocalStorage.getItem(storageKey);

        if (cxDeviceId == null) {
            cxDeviceId = generateUUID();
            guardedLocalStorage.setItem(storageKey, cxDeviceId);
        }

        return cxDeviceId;
    };

    private stringToArray = (string) => {
        const buffer = new ArrayBuffer(string.length * 2); // 2 bytes for each char
        const array = new Uint16Array(buffer);
        for (let i = 0, strLen = string.length; i < strLen; i += 1) {
            array[i] = string.charCodeAt(i);
        }
        return array;
    };

    handleWidevine = () => {
        const { DEVICE_TYPE, WIDEVINE } = localConfig.drm ?? {};

        this.drmType = WIDEVINE;
        this.deviceId = this.getOrCreateCxDeviceId(DEVICE_TYPE);
    };

    handlePlayReady = () => {
        const { DEVICE_TYPE, PLAYREADY } = localConfig.drm ?? {};

        this.drmType = PLAYREADY;
        this.deviceId = this.getOrCreateCxDeviceId(DEVICE_TYPE);
    };

    handleFairPlay = () => {
        const { DEVICE_TYPE, FAIRPLAY } = localConfig.drm ?? {};

        this.drmType = FAIRPLAY;
        this.deviceId = this.getOrCreateCxDeviceId(DEVICE_TYPE);
    };

    private preloadMediaKeys = async (access: MediaKeySystemAccess): Promise<void> => {
        if (this.mediaKeysPreloaded) {
            return;
        }

        this.mediaKeysPreloaded = true;

        try {
            await access.createMediaKeys();
        } catch {
            this.mediaKeysPreloaded = false;
        }
    };

    detectSupport = (): void => {
        const { KEY_SYSTEM_PLAYREADY, KEY_SYSTEM_WIDEVINE } = localConfig.drm;

        if (navigator.requestMediaKeySystemAccess !== undefined && !isSafari()) {
            if (window.Cypress) {
                this.handleWidevine();
                return;
            }

            const widevineOptions = [
                {
                    initDataTypes: ['cenc'],
                    videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.4d401e"' }],
                },
            ];

            navigator
                .requestMediaKeySystemAccess(KEY_SYSTEM_WIDEVINE, widevineOptions)
                .then((access) => {
                    this.handleWidevine();
                    return this.preloadMediaKeys(access);
                })
                .catch(() => {});
            navigator
                .requestMediaKeySystemAccess(KEY_SYSTEM_PLAYREADY, [{}])
                .then(this.handlePlayReady)
                .catch(() => {});
        } else if (isSafari()) {
            this.handleFairPlay();
        } else {
            console.error('Not supported DRM');
        }
    };

    getConfiguration = (asset: PlayerAsset) => {
        const { KEY_SYSTEM_PLAYREADY, KEY_SYSTEM_WIDEVINE, KEY_FAIRPLAY } = localConfig.drm;

        return {
            drm: {
                preferredKeySystems: [isSafari() ? KEY_FAIRPLAY : KEY_SYSTEM_WIDEVINE],
                servers: {
                    [KEY_SYSTEM_WIDEVINE]: asset.drm_license_url,
                    [KEY_SYSTEM_PLAYREADY]: asset.drm_license_url,
                    [KEY_FAIRPLAY]: asset.drm_license_url,
                },
            },
            streaming: {
                stallEnabled: true,
                stallThreshold: 1,
                stallSkip: 0.1,
                gapDetectionThreshold: 0.5,
            },
        };
    };

    getAdditionalConfiguration = async (asset: PlayerAsset): Promise<{ [p: string]: any }> => {
        if (!isSafari()) {
            return {};
        }

        const req = await fetch(asset.drm_license_url, {
            method: 'post',
        });

        if (!req.ok) {
            let errorCode = 'UNKNOWN_ERROR';
            try {
                const errorBody = await req.json();
                if (errorBody?.error_code) {
                    errorCode = errorBody.error_code;
                }
            } catch {
                /* failed to parse error body */
            }
            const error: any = new Error(errorCode);
            error.code = req.status;
            error.category = 'drm';
            error.data = [errorCode];
            throw error;
        }

        const cert = await req.arrayBuffer();

        return {
            'drm.advanced.com\\.apple\\.fps\\.1_0.serverCertificate': new Uint8Array(cert),
            'drm.initDataTransform': (initData, initDataType) => {
                if (initDataType !== 'skd') {
                    return initData;
                }

                const skdUri = shaka.util.StringUtils.fromBytesAutoDetect(initData);
                const skdValue = skdUri.split('skd://')[1].split('?')[0];
                const skdValueAsBytes = this.stringToArray(window.atob(skdValue));

                return shaka.drm.FairPlay.initDataTransform(initData, skdValueAsBytes, cert);
            },
        };
    };

    getHeaders = (asset: PlayerAsset) => {
        const { CUSTOM_HEADER_KEY } = localConfig.drm;

        return asset.drm_license_context ? { [CUSTOM_HEADER_KEY]: asset.drm_license_context } : {};
    };

    getResponseFilter = (type, response): void => {
        if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) {
            return;
        }

        if (isSafari()) {
            let responseText = shaka.util.StringUtils.fromUTF8(response.data);
            responseText = responseText.trim();
            response.data = shaka.util.Uint8ArrayUtils.fromBase64(JSON.parse(responseText).CkcMessage).buffer;
        }
    };

    getRequestFilter = (type, request, asset): void => {
        if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
            request.headers = { ...request.headers, ...this.getHeaders(asset) };
        }
    };

    getDeviceId = () => {
        const { DEVICE_TYPE } = localConfig.drm ?? {};
        this.deviceId = this.getOrCreateCxDeviceId(DEVICE_TYPE);
        return this.deviceId;
    };

    private registerNetworkFilters = (playerInstance: any) => {
        if (this.filtersRegisteredOn === playerInstance) {
            return;
        }

        const networkingEngine = playerInstance.getNetworkingEngine();

        if (!networkingEngine) {
            return;
        }

        networkingEngine.registerRequestFilter((type, request) => this.getRequestFilter(type, request, this.currentAsset));
        networkingEngine.registerResponseFilter(this.getResponseFilter);

        this.filtersRegisteredOn = playerInstance;
    };

    configurePlayer = async (playerInstance: any, asset: PlayerAsset) => {
        this.currentAsset = asset;

        const additionalConfig = await this.getAdditionalConfiguration(asset);
        const configKeys = Object.keys(additionalConfig);

        if (configKeys.length) {
            configKeys.forEach((key) => playerInstance.configure(key, additionalConfig[key]));
        }

        if (isSafari()) {
            shaka.polyfill.PatchedMediaKeysApple.install();
        }

        playerInstance.configure(this.getConfiguration(asset));
        this.registerNetworkFilters(playerInstance);
    };

    getDRMType = () => this.drmType;

    /**
     * Initialize DRM: detect browser support.
     */
    init = () => {
        this.detectSupport();
    };
}

export default new DRM();
